import { refreshAccessToken } from '@shared/auth/authentication.hooks';
import { getRefreshToken, isRefreshTokenUsable } from './tokens';
import { report } from '@shared/observability';

/**
 * Single in-flight refresh promise shared by every caller. Without this, N
 * requests that all hit a 401 at the same moment each start their own
 * refresh call — wasted requests at best, a race that can invalidate a
 * token another caller is mid-refresh with at worst. Every caller within
 * the same tick gets the one promise; the next 401 after it settles starts
 * a fresh refresh.
 */
let inFlightRefresh: Promise<boolean> | null = null;

export function refreshTokenSingleFlight(): Promise<boolean> {
	if (!inFlightRefresh) {
		inFlightRefresh = performRefresh().finally(() => {
			inFlightRefresh = null;
		});
	}

	return inFlightRefresh;
}

async function performRefresh(): Promise<boolean> {
	if (!isRefreshTokenUsable()) {
		return false;
	}

	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		return false;
	}

	try {
		await refreshAccessToken(refreshToken);
		return true;
	} catch {
		// Reported here specifically, not for the two early returns above --
		// "no usable refresh token" is a normal state (a logged-out reader
		// hitting an authenticated endpoint), not a failure. This branch is
		// a refresh token that existed and was attempted, but the IdP call
		// itself failed -- usually a broken IdP config for the tenant,
		// worth knowing about before ten users call support about it.
		report({ name: 'auth_refresh_exhausted' });
		return false;
	}
}
